import React, { useEffect, useMemo, useCallback, useState } from 'react';
import ActionButton from '../common/ActionButton';
import TabPanel from '../common/TabPanel';
import { MODAL_ACTION_TYPE } from '../../utils/constants/modal';
import {
    BarcodeOutlined,
    CarOutlined,
    ScanOutlined,
    SearchOutlined,
    UserOutlined
} from '@ant-design/icons';
import {
    CreateUpdateDeviceModal,
    CreateUpdateVehicleModal,
    DeviceTable,
    GuestsTable,
    Loader,
    OrganizationUsersTable,
    SuspendModal,
    VehicleTable,
    VisitorsTable
} from '../index';
import OccupantVehicleService from '../../services/occupant-vehicle.service';
import {
    createVehiclePlate,
    createVehiclePlateForRealtimeDatabase,
    isPlateUnique
} from '../../utils/helpers/vehicle.helper';
import { ORG_PROPERTY_TAB } from '../../utils/constants/property';
import { connect, useSelector } from 'react-redux';
import {
    getPropertyInvitedMembers,
    makeGetOrgMembers,
    makeGetPropertyAssociates,
    makeGetPropertyGuests
} from '../../store/selectors/Property';
import { openCompactModal, openModal } from '../../utils/modal/modal.helper';
import {
    removingMember,
    removingPending,
    suspendingPropertyGuest
} from '../../store/actions/Members';
import { getPropertyByIdCollection } from '../../store/actions/Properties';
import { getPropertyMembers } from '../../store/actions/VirtualGuard';
import DeleteModal from '../DeleteModal';
import { getOrgVehiclePlates, getPropertyVehicles } from '../../store/selectors/Vehicle';
import PropertyVisitorService from '../../services/property-visitor.service';
import { openSnackbar } from 'store/actions/Snackbar';
import UpdateUserModal from 'sections/view-edit-org/UpdateUserModal';
import InviteGuestModal from '../Guests/InviteGuestModal';
import InviteAssociateModal from './InviteAssociateModal';
import AssociatesTable from './AssociatesTable';
import InvitedOrgMembersTable from './InvitedOrgMemebersTable';
import { setTableIndex, setRowCount } from 'store/actions/Table';
import AddEditOtcModal from 'components/Otc/AddEditOtcModal';
import CodesTable from './CodesTable';
import { DeleteOutline } from '@mui/icons-material';
import InviteVisitorModal from 'components/Guests/InviteVisitorModal';
import InvitesService from 'services/invites.service';
import SnackbarNotify from 'utils/snackbar/SnackbarNotify';
import { InputAdornment, TextField } from '@mui/material';
import OrgService from 'services/org.service';
import OrgPropertiesService from 'services/org-properties.service';
import { usePermissions } from 'hooks/usePermissions';
import { normalizeOwners } from 'utils/devices/normalizeOwners';
import { getDeviceAssignEligibility } from 'utils/devices/deviceAssignEligibility';
import { OrgPermissions, moduleKeys } from 'utils/permissions';
import { excludeHiddenEmails } from 'utils/helpers/email.helper';
import {
    managerLimitErrorMessage,
    userCannotBeAddedMessage
} from 'utils/helpers/manager-limit.helper';
import OrgPropertyVisitorService from 'services/org-property-visitor.service';
import capitalize from 'utils/capitalize';
import { userRoles } from '../../utils/constants';
import { processDeviceData } from 'components/Property/PropertyTabPanel';
import DeviceTypesService from 'services/device-types.service';

const MyOrganizationTabPanel = ({
    title,
    activeOrg,
    property,
    getPropertyByIdCollection,
    getPropertyMembers,
    removingMember,
    removingPending,
    suspendingPropertyGuest,
    propertyVehicles,
    orgVehiclePlates,
    devices,
    openSnackbar,
    membersLoading,
    propertiesLoading,
    otcCodes,
    otcPage,
    otcRow,
    setTableIndex,
    setRowCount,
    changingFocus,
    orgInviteMembers,
    orgInviteOperators
}) => {
    const [deviceTypes, setDeviceTypes] = useState({});
    useEffect(() => {
        let cancelled = false;
        DeviceTypesService.getDeviceTypes()
            .then(data => {
                if (!cancelled) setDeviceTypes(data || {});
            })
            .catch(err => {
                if (!cancelled) {
                    SnackbarNotify.error(`Failed to load device types: ${err?.message || 'Unexpected error'}`);
                }
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const propertyId = activeOrg?.property_id;
    const allUsers = excludeHiddenEmails(activeOrg?.users || []);
    const activeManagers = allUsers.filter(user => user.active);
    const legacyInvitedManagers = allUsers.filter(user => !user.active);
    const invitedManagers = useMemo(() => {
        const v2ManagerInvites = excludeHiddenEmails(
            (orgInviteOperators || []).filter(
                invite =>
                    (!propertyId || invite.property_id === propertyId) &&
                    ['invited', 'pending_approval'].includes(invite.status)
            )
        );

        const legacyEmails = new Set(
            legacyInvitedManagers.map(user => user.email).filter(Boolean)
        );

        return [
            ...legacyInvitedManagers,
            ...v2ManagerInvites.filter(invite => !legacyEmails.has(invite.email))
        ];
    }, [legacyInvitedManagers, orgInviteOperators, propertyId]);
    const EMPTY = Object.freeze([]);

    const selectOrgMembers = useMemo(makeGetOrgMembers, []);
    const selectPropertyGuests = useMemo(makeGetPropertyGuests, []);
    const selectPropertyAssociates = useMemo(makeGetPropertyAssociates, []);

    const occupants = useSelector(state =>
        propertyId ? selectOrgMembers(state, propertyId) : EMPTY
    );

    const guests = useSelector(state =>
        propertyId ? selectPropertyGuests(state, propertyId) : EMPTY
    );

    const associates = useSelector(state =>
        propertyId ? selectPropertyAssociates(state, propertyId) : EMPTY
    );

    const invitedMembersRaw = useSelector(getPropertyInvitedMembers);

    const invitedMembers = useMemo(
        () =>
            excludeHiddenEmails(
                (invitedMembersRaw || []).filter(
                    m => m.role !== userRoles.OPERATOR && m.role !== userRoles.ASSOCIATE
                )
            ),
        [invitedMembersRaw]
    );

    const invitedAssociates = useMemo(
        () =>
            excludeHiddenEmails(
                (invitedMembersRaw || []).filter(m => m.role === userRoles.ASSOCIATE)
            ),
        [invitedMembersRaw]
    );

    const { hasPermission } = usePermissions();

    const [loading, setLoading] = useState(false);
    const [visitors, setVisitors] = useState(null);
    const [selectedOtcCodes, setSelectedOtcCodes] = useState([]);
    const [globalFilter, setGlobalFilter] = useState('');
    const [visitorLoading, setVisitorLoading] = useState(false);

    const fetchVisitors = useCallback(async () => {
        if (!propertyId) return;

        setLoading(true);
        try {
            const response = await PropertyVisitorService.getVisitors(propertyId);
            if (response) {
                setVisitors(response);
            }
        } catch (error) {
            console.error('Failed to fetch visitors:', error);
        } finally {
            setLoading(false);
        }
    }, [propertyId]);

    useEffect(() => {
        fetchVisitors();
    }, [fetchVisitors]);

    useEffect(() => {
        if (propertyId) {
            getPropertyByIdCollection(propertyId);
            getPropertyMembers({ propertyId });
        }
    }, [propertyId, getPropertyByIdCollection, getPropertyMembers]);

    const handleRemoveManager = async manager => {
        setLoading(true);
        try {
            await OrgService.removeOrgManager(activeOrg.org_id, manager.id);

            SnackbarNotify.success(
                `Manager ${capitalize(manager.first_name)} ${capitalize(
                    manager.last_name
                )} has been removed.`
            );
        } catch (error) {
            console.error('Error removing manager:', error);
            SnackbarNotify.error(
                `Error removing manager: ${error?.message || 'Unexpected error'}`
            );
        } finally {
            setLoading(false);
        }
    };

    const handleSuspendVisitor = async visitor => {
        setVisitorLoading(true);
        const isSuspended = visitor.suspended;
        try {
            await OrgPropertyVisitorService.suspendOrgVisitor(
                activeOrg.org_id,
                propertyId,
                visitor.id,
                !isSuspended
            );

            SnackbarNotify.success(
                `Visitor ${capitalize(visitor.first_name)} ${capitalize(
                    visitor.last_name
                )} has been ${isSuspended ? 'unsuspended' : 'suspended'}.`
            );
        } catch (error) {
            console.error('Error adding visitor:', error);
            SnackbarNotify.error(
                `Error ${isSuspended ? 'unsuspending' : 'suspending'} visitor: ${
                    error?.message || 'Unexpected error'
                }`
            );
        } finally {
            setVisitorLoading(false);
        }
        fetchVisitors();
    };

    const handleRemoveVisitor = async visitor => {
        setVisitorLoading(true);
        try {
            await OrgPropertyVisitorService.removeOrgVisitor(
                activeOrg.org_id,
                propertyId,
                visitor.id
            );

            SnackbarNotify.success(
                `Visitor ${capitalize(visitor.first_name)} ${capitalize(
                    visitor.last_name
                )} has been deleted.`
            );
        } catch (error) {
            console.error('Error adding visitor:', error);
            SnackbarNotify.error(
                `Error removing visitor: ${error?.message || 'Unexpected error'}`
            );
        } finally {
            setVisitorLoading(false);
        }
        fetchVisitors();
    };

    const handleRemoveSelected = async () => {
        if (!selectedOtcCodes.length) return;

        const handleBulkDelete = async () => {
            setLoading(true);
            try {
                const codeIds = selectedOtcCodes.map(otc => otc.code_id);
                const orgId = selectedOtcCodes[0]?.org_id;

                if (!orgId) {
                    throw new Error('Org ID not found in selected OTCs');
                }

                await OrgService.revokeMultipleOrgOtcs(orgId, codeIds);
                SnackbarNotify.success('One-Time Codes successfully revoked');
                setSelectedOtcCodes([]);
            } catch (error) {
                console.error('Failed to revoke OTCs:', error);
                SnackbarNotify.error(
                    `Error revoking one-time codes: ${
                        error?.message || 'Unexpected error'
                    }`
                );
                throw error;
            } finally {
                setLoading(false);
            }
        };

        openCompactModal(DeleteModal, {
            title:
                selectedOtcCodes.length === 1
                    ? selectedOtcCodes[0].name
                    : `${selectedOtcCodes.length} codes`,
            body: 'Revoke Selected Codes',
            actionLabel: 'Revoke',
            onDelete: handleBulkDelete
        });
    };

    const refreshPropertyData = () => {
        getPropertyByIdCollection(property.id);
    };

    // const onDeleteManager = id => {
    //     removeOrgUser({
    //         ...activeManagers.find(user => user.id === id),
    //         org_id: activeOrg.org_id
    //     });
    // };

    
    const handleOpenMemberModal = (user, type = MODAL_ACTION_TYPE.ADD) => {
        const handleUpdatedOrCreateUser = async newUser => {
            setLoading(true);
            try {
                if (type === MODAL_ACTION_TYPE.ADD) {
                    const roleKey = newUser.type || 'manager';
                    const matchedRole = (activeOrg.roles || []).find(
                        r => r.key === roleKey
                    );
                    const permissions = [...new Set(matchedRole?.permissions || [])];
                    await OrgPropertiesService.createUserInvite(
                        activeOrg.org_id,
                        propertyId,
                        {
                            first_name: newUser.first_name,
                            last_name: newUser.last_name,
                            email: newUser.email,
                            phone: newUser.phone || null,
                            company_name: newUser.company_name || null,
                            role: userRoles.OPERATOR,
                            type: roleKey,
                            requires_approval: false,
                            permissions,
                            primary: newUser.type === 'manager' || !!newUser.primary
                        }
                    );
                } else {
                    await OrgService.updateOrgManager(activeOrg.org_id, user.id, newUser);
                }
                SnackbarNotify.success(
                    type === MODAL_ACTION_TYPE.ADD
                        ? `Invite sent to ${capitalize(newUser.first_name)} ${capitalize(
                              newUser.last_name
                          )} — pending acceptance.`
                        : `Manager ${capitalize(newUser.first_name)} ${capitalize(
                              newUser.last_name
                          )} has been updated.`
                );
                setLoading(false);
            } catch (error) {
                const message = Array.isArray(error.message)
                    ? error.message.join(', ')
                    : error.message;

                console.error('Error:', message);
                // A manager refused for the org's limit (or a locked primary), or a user the API will not add, shows its
                // userMessage; the reason stays in `message`, in the console.
                SnackbarNotify.error(
                    managerLimitErrorMessage(error) ||
                        userCannotBeAddedMessage(error) ||
                        `Something went wrong - ${message}`
                );
                setLoading(false);
            }
        };
        openModal(
            UpdateUserModal,
            {
                user,
                type,
                onCreateOrUpdate: handleUpdatedOrCreateUser,
                onDelete: handleRemoveManager,
                roles: activeOrg.roles
            },
            { fullWidth: true }
        );
    };

    const handleOpenGuestsModal = (guestInvite, type = MODAL_ACTION_TYPE.ADD) => {
        const handleDeleteGuestInvite = async invitePayload => {
            setLoading(true);

            try {
                const inviteOrgId = invitePayload?.org_id || activeOrg?.org_id;
                const invitePropertyId = invitePayload?.property_id || propertyId;
                const inviteId = invitePayload?.invite_id || invitePayload?.id;

                await InvitesService.deleteInvite(
                    inviteOrgId,
                    invitePropertyId,
                    inviteId
                );
            } finally {
                setLoading(false);
            }
        };

        const handleInviteGuest = async guestPayload => {
            setLoading(true);
            try {
                if (type === MODAL_ACTION_TYPE.ADD) {
                    await OrgPropertiesService.createUserInvite(
                        property.org_id,
                        propertyId,
                        guestPayload
                    );
                }
            } finally {
                setLoading(false);
            }
        };

        openModal(InviteGuestModal, {
            type,
            guestInvite,
            onSubmit: handleInviteGuest,
            onDelete: handleDeleteGuestInvite
        });
    };

    const handleOpenAssociateModal = () => {
        const handleInviteAssociate = async associatePayload => {
            setLoading(true);
            try {
                await OrgPropertiesService.createUserInvite(
                    activeOrg.org_id,
                    propertyId,
                    associatePayload
                );
            } finally {
                setLoading(false);
            }
        };

        openModal(InviteAssociateModal, { onSubmit: handleInviteAssociate });
    };

    const handleSuspendAssociate = async member => {
        const suspended = !member?.properties?.[propertyId]?.suspended;

        setLoading(true);
        try {
            await OrgPropertiesService.updateUser(
                activeOrg.org_id,
                propertyId,
                member.uid,
                { suspended }
            );
            SnackbarNotify.success(
                `Associate ${capitalize(member.first_name)} ${capitalize(
                    member.last_name
                )} has been ${suspended ? 'suspended' : 'unsuspended'}.`
            );
        } catch (error) {
            console.error('Error updating associate:', error);
            SnackbarNotify.error(
                `Error ${suspended ? 'suspending' : 'unsuspending'} associate: ${
                    error?.message || 'Unexpected error'
                }`
            );
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteAssociate = member => {
        openModal(DeleteModal, {
            title: `${member.first_name} ${member.last_name}`,
            body: 'Remove Associate',
            actionLabel: 'Remove',
            custom: {
                postfix:
                    '? Their devices in this organization are removed too. This action cannot be undone.'
            },
            onDelete: async () => {
                try {
                    await OrgPropertiesService.removeUserAccess(
                        activeOrg.org_id,
                        propertyId,
                        member.uid
                    );
                    SnackbarNotify.success('Associate removed.');
                } catch (error) {
                    console.error('Error removing associate:', error);
                    SnackbarNotify.error(
                        `Error removing associate: ${
                            error?.message || 'Unexpected error'
                        }`
                    );
                }
            }
        });
    };

    const handleOpenVisitorsModal = (guestInvite, type = MODAL_ACTION_TYPE.ADD) => {
        const handleDeleteVisitorInvite = async invitePayload => {
            setLoading(true);

            try {
                await OrgPropertyVisitorService.removeOrgVisitor(
                    activeOrg.org_id,
                    propertyId,
                    invitePayload.id
                );
                SnackbarNotify.success(`Successfully removed visitor!`);
            } catch (error) {
                console.error('Error adding visitor:', error);
                SnackbarNotify.error(
                    `Error removing visitor: ${error?.message || 'Unexpected error'}`
                );
            } finally {
                setLoading(false);
            }
            fetchVisitors();
        };
        const handleInviteVisitor = async visitorPayload => {
            setLoading(true);

            try {
                if (type === MODAL_ACTION_TYPE.ADD) {
                    await OrgPropertyVisitorService.addOrgVisitor(
                        activeOrg.org_id,
                        propertyId,
                        visitorPayload
                    );
                    SnackbarNotify.success(`Successfully added visitor!`);
                } else {
                    await OrgPropertyVisitorService.updateOrgVisitor(
                        activeOrg.org_id,
                        propertyId,
                        guestInvite.id,
                        visitorPayload
                    );
                    SnackbarNotify.success(`Successfully updated visitor!`);
                }
            } catch (error) {
                console.error('Error adding visitor:', error);
                SnackbarNotify.error(
                    `Error adding visitor: ${error?.message || 'Unexpected error'}`
                );
            } finally {
                setLoading(false);
            }
            fetchVisitors();
        };
        openModal(InviteVisitorModal, {
            type,
            guestInvite,
            onSubmit: handleInviteVisitor,
            onDelete: handleDeleteVisitorInvite
        });
    };

    const handleOpenDeviceModal = (device, type = MODAL_ACTION_TYPE.ADD) => {
        const eligibility = getDeviceAssignEligibility(activeOrg?.modules);
        const deviceObj = device || {
            owner: null,
            facility_code: '',
            device_number: '',
            access_groups: [],
            device_type: '',
            vehicle: null
        };

        openModal(CreateUpdateDeviceModal, {
            selectedDevice: deviceObj,
            type,
            activeOrgId: activeOrg?.org_id,
            activeOrg,
            propertyId: property?.id,
            owners: normalizeOwners({
                members: [...(eligibility.operators ? occupants : []), ...associates],
                invitedMembers: [
                    ...(eligibility.invitedOccupants
                        ? invitedMembers.filter(m => m.role !== userRoles.GUEST)
                        : []),
                    ...invitedAssociates
                ],
                guests: eligibility.orgGuests ? guests : [],
                orgPropertyId: propertyId
            }),
            deviceTypes,
            openSnackbar,
            refreshPropertyData
        });
    };

    const handleOpenVehicleModal = (
        vehicle = {
            country: 'USA'
        },
        modalType = MODAL_ACTION_TYPE.ADD
    ) => {
        const orgId = activeOrg.org_id;
        const isEdit = modalType === MODAL_ACTION_TYPE.EDIT;

        const handleSubmit = async (vehiclePayload, onClose) => {
            const vehicleFields = {
                tag: vehiclePayload.tag,
                make: vehiclePayload.make,
                model: vehiclePayload.model,
                state: vehiclePayload.state,
                ...(vehiclePayload.country != null
                    ? { country: vehiclePayload.country }
                    : {}),
                year:
                    vehiclePayload.year != null && vehiclePayload.year !== ''
                        ? String(vehiclePayload.year)
                        : '',
                color:
                    vehiclePayload.color != null && vehiclePayload.color !== ''
                        ? vehiclePayload.color
                        : ''
            };
            const newOwnerId = vehiclePayload.ownerId;
            const ownerChanged = isEdit && newOwnerId && newOwnerId !== vehicle.ownerId;

            const newPlate = createVehiclePlate(vehicleFields);
            const normalizedNewPlate = createVehiclePlateForRealtimeDatabase(vehicleFields);
            const plateChanged = !isEdit || createVehiclePlate(vehicle) !== newPlate;

            if (!isPlateUnique(normalizedNewPlate, orgVehiclePlates) && plateChanged) {
                openSnackbar({
                    open: true,
                    message: `Vehicle plate "${newPlate}" already exists in the organization`,
                    variant: 'alert',
                    alert: { color: 'error' }
                });
                return;
            }

            try {
                if (isEdit && !ownerChanged) {
                    await OccupantVehicleService.updateVehicle(
                        orgId,
                        vehicle.ownerId,
                        vehicle.tag,
                        vehicleFields
                    );
                } else if (ownerChanged) {
                    // Owner changed → move the vehicle. Add it to the NEW owner FIRST, then remove
                    // it from the OLD owner, so a failed add never leaves the vehicle orphaned off
                    // both members. A 409 means the vehicle already exists on the new owner → the
                    // move is effectively done, so fall through and detach it from the old owner;
                    // any other error aborts the move with the vehicle still safely on the old owner.
                    try {
                        await OccupantVehicleService.addVehicle(orgId, newOwnerId, vehicleFields);
                    } catch (addError) {
                        if (addError?.statusCode !== 409) throw addError;
                    }
                    await OccupantVehicleService.deleteVehicle(orgId, vehicle.ownerId, vehicle.tag);
                } else {
                    await OccupantVehicleService.addVehicle(orgId, newOwnerId, vehicleFields);
                }

                openSnackbar({
                    open: true,
                    message: `Vehicle ${isEdit ? 'updated' : 'added'} successfully`,
                    variant: 'alert',
                    alert: { color: 'success' }
                });
                onClose?.();
            } catch (error) {
                openSnackbar({
                    open: true,
                    message:
                        error?.userMessage ||
                        error?.message ||
                        `Failed to ${isEdit ? 'update' : 'add'} vehicle`,
                    variant: 'alert',
                    alert: { color: 'error' }
                });
            }
        };

        const handleDelete = (_vehicle, closeVehicleModal) => {
            openCompactModal(DeleteModal, {
                title: `${vehicle.tag}`,
                body: 'Delete Vehicle',
                onDelete: async () => {
                    try {
                        await OccupantVehicleService.deleteVehicle(orgId, vehicle.ownerId, vehicle.tag);
                        openSnackbar({
                            open: true,
                            message: 'Vehicle deleted successfully',
                            variant: 'alert',
                            alert: { color: 'success' }
                        });
                        closeVehicleModal?.();
                    } catch (error) {
                        openSnackbar({
                            open: true,
                            message:
                                error?.userMessage || error?.message || 'Failed to delete vehicle',
                            variant: 'alert',
                            alert: { color: 'error' }
                        });
                    }
                }
            });
        };

        openModal(CreateUpdateVehicleModal, {
            vehicle: vehicle,
            users: occupants,
            onSubmit: handleSubmit,
            onDelete: handleDelete,
            modalType
        });
    };

    const handleOpenOtcModal = (otc = null, type = MODAL_ACTION_TYPE.ADD) => {
        // const isEdit = type === MODAL_ACTION_TYPE.EDIT;

        const handleCreateOrUpdate = async (payload, actionType) => {
            setLoading(true);
            try {
                if (actionType === 'edit') {
                    await OrgService.updateOrgOtc(otc.org_id, otc.code_id, payload);
                    return await OrgService.getOrgOtcById(otc.org_id, otc.code_id);
                }

                // Create new OTC
                return await OrgService.createOrgOtc(activeOrg.org_id, {
                    ...payload,
                    property_id: activeOrg.property_id
                });
            } catch (error) {
                console.error(`Failed to ${actionType} OTC:`, error);
                throw error;
            } finally {
                setLoading(false);
            }
        };

        const handleDelete = (otcToRevoke, closeOtcModal) => {
            openCompactModal(DeleteModal, {
                title: otcToRevoke.name,
                body: 'Revoke Code',
                actionLabel: 'Revoke',
                onDelete: async () => {
                    setLoading(true);
                    try {
                        await OrgService.revokeOrgOtc(
                            otcToRevoke.org_id,
                            otcToRevoke.code_id
                        );
                        SnackbarNotify.success('One-Time Code successfully revoked');
                        closeOtcModal?.();
                    } catch (error) {
                        console.error('Failed to revoke OTC:', error);
                        SnackbarNotify.error(
                            `Error revoking one-time code: ${
                                error?.message || 'Unexpected error'
                            }`
                        );
                        throw error;
                    } finally {
                        setLoading(false);
                    }
                }
            });
        };

        openModal(
            AddEditOtcModal,
            {
                otc,
                type,
                activeOrg,
                onCreateOrUpdate: handleCreateOrUpdate,
                onDelete: handleDelete
            },
            { fullWidth: true }
        );
    };

    const handleDeletePropertyGuest = member => {
        openModal(DeleteModal, {
            title: `${member.first_name} ${member.last_name}`,
            body: 'Remove Guest Access',
            onDelete: () => {
                removingMember({
                    member: member,
                    orgId: activeOrg.org_id,
                    propertyId
                });
            }
        });
    };

    const handleDeleteInvitedMember = member => {
        const inviteOrgId = member?.org_id || activeOrg?.org_id;
        const invitePropertyId = member?.property_id || propertyId;
        const inviteId = member?.invite_id || member?.id;

        openModal(DeleteModal, {
            title: `${member.first_name} ${member.last_name}`,
            body: 'Remove Invitation',
            onDelete: async () => {
                try {
                    await InvitesService.deleteInvite(
                        inviteOrgId,
                        invitePropertyId,
                        inviteId
                    );
                    SnackbarNotify.success('Invitation removed.');
                } catch (error) {
                    console.error('Error removing invitation:', error);
                    SnackbarNotify.error(
                        `Error removing invitation: ${
                            error?.message || 'Unexpected error'
                        }`
                    );
                }
            }
        });
    };

    const handleApproveInvitedMember = async member => {
        const inviteOrgId = member?.org_id || activeOrg?.org_id;
        const invitePropertyId = member?.property_id || propertyId;
        const inviteId = member?.invite_id || member?.id;

        setLoading(true);
        try {
            await InvitesService.approveInvite(inviteOrgId, invitePropertyId, inviteId);
            SnackbarNotify.success(
                `${capitalize(member?.first_name || '')} ${capitalize(
                    member?.last_name || ''
                )} — invite sent.`.trim()
            );
        } catch (error) {
            console.error('Error approving invitation:', error);
            SnackbarNotify.error(
                `Error approving invitation: ${error?.message || 'Unexpected error'}`
            );
        } finally {
            setLoading(false);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const TABS = {
        [ORG_PROPERTY_TAB.TEAM_MEMBERS]: {
            label: 'Team Members',
            count: activeManagers?.length || 0,
            action: hasPermission(OrgPermissions.MANAGE_OPERATORS)
                ? {
                      label: 'Add Team Member',
                      icon: <UserOutlined />,
                      handler: () => handleOpenMemberModal()
                  }
                : null,
            content: (
                <OrganizationUsersTable
                    users={activeManagers}
                    globalFilter={globalFilter}
                    onEditUser={
                        hasPermission(OrgPermissions.MANAGE_OPERATORS)
                            ? user => {
                                  handleOpenMemberModal(user, MODAL_ACTION_TYPE.EDIT);
                              }
                            : null
                    }
                />
            )
        },
        [ORG_PROPERTY_TAB.GUESTS]: {
            label: 'Guests',
            count: guests?.length || 0,
            action: hasPermission(OrgPermissions.MANAGE_ORG_GUESTS)
                ? {
                      label: 'Add Guest',
                      icon: <UserOutlined />,
                      handler: () => handleOpenGuestsModal()
                  }
                : null,
            content: (
                <GuestsTable
                    members={guests}
                    propertyId={propertyId}
                    canManage={hasPermission(OrgPermissions.MANAGE_ORG_GUESTS)}
                    globalFilter={globalFilter}
                    onGuestSuspend={member => {
                        openModal(SuspendModal, {
                            title: `${member?.first_name} ${member?.last_name}`,
                            body: `Are you sure you want to ${
                                member?.properties[propertyId]?.suspended
                                    ? 'unsuspend'
                                    : 'suspend'
                            } this guest?`,
                            suspended: member?.properties[propertyId]?.suspended,
                            onSuspend: () => {
                                suspendingPropertyGuest({
                                    member: {
                                        ...member,
                                        suspended:
                                            !member?.properties[propertyId]?.suspended
                                    },
                                    orgId: activeOrg.org_id,
                                    propertyId: propertyId
                                });
                            }
                        });
                    }}
                    onGuestDelete={member => handleDeletePropertyGuest(member)}
                />
            )
        },
        [ORG_PROPERTY_TAB.ASSOCIATES]: {
            label: 'Associates',
            count: associates.length + invitedAssociates.length,
            action: hasPermission(OrgPermissions.MANAGE_ORG_GUESTS)
                ? {
                      label: 'Invite Associate',
                      icon: <UserOutlined />,
                      handler: () => handleOpenAssociateModal()
                  }
                : null,
            content: (
                <AssociatesTable
                    members={associates}
                    invites={invitedAssociates}
                    propertyId={propertyId}
                    canManage={hasPermission(OrgPermissions.MANAGE_ORG_GUESTS)}
                    globalFilter={globalFilter}
                    onSuspend={member => {
                        openModal(SuspendModal, {
                            title: `${member?.first_name} ${member?.last_name}`,
                            body: `Are you sure you want to ${
                                member?.properties[propertyId]?.suspended
                                    ? 'unsuspend'
                                    : 'suspend'
                            } this associate?`,
                            suspended: member?.properties[propertyId]?.suspended,
                            onSuspend: () => handleSuspendAssociate(member)
                        });
                    }}
                    onDelete={member => handleDeleteAssociate(member)}
                    onInviteDelete={member => handleDeleteInvitedMember(member)}
                />
            )
        },
        [ORG_PROPERTY_TAB.VISITORS]: {
            label: 'Visitors',
            count: visitors?.length || 0,
            action: hasPermission(OrgPermissions.MANAGE_ORG_GUESTS)
                ? {
                      label: 'Add Visitor',
                      icon: <UserOutlined />,
                      handler: () => handleOpenVisitorsModal()
                  }
                : null,
            content: (
                <VisitorsTable
                    visitors={visitors || []}
                    canManage={hasPermission(OrgPermissions.MANAGE_ORG_GUESTS)}
                    onVisitorClick={
                        hasPermission(OrgPermissions.MANAGE_ORG_GUESTS)
                            ? guestInvite =>
                                  handleOpenVisitorsModal(
                                      guestInvite,
                                      MODAL_ACTION_TYPE.EDIT
                                  )
                            : null
                    }
                    onRemove={handleRemoveVisitor}
                    onSuspend={handleSuspendVisitor}
                    globalFilter={globalFilter}
                />
            )
        },
        [ORG_PROPERTY_TAB.INVITED]: {
            label: 'Invited',
            count: [...(invitedMembers || []), ...(invitedManagers || [])].length,
            content: (
                <InvitedOrgMembersTable
                    members={invitedMembers}
                    invitedManagers={invitedManagers}
                    onInvitedMemberDelete={member => handleDeleteInvitedMember(member)}
                    onInvitedMemberApprove={member => handleApproveInvitedMember(member)}
                    onDeleteManager={handleRemoveManager}
                    globalFilter={globalFilter}
                    canManage={hasPermission(OrgPermissions.MANAGE_OPERATORS)}
                    onEditManagerInvite={invite => {
                        handleOpenMemberModal(invite, MODAL_ACTION_TYPE.EDIT);
                    }}
                    onEditGuestInvite={invite => {
                        handleOpenGuestsModal(invite, MODAL_ACTION_TYPE.EDIT);
                    }}
                />
            )
        },
        [ORG_PROPERTY_TAB.VEHICLES]: {
            label: 'Vehicles',
            count: propertyVehicles?.length || 0,
            action: hasPermission(OrgPermissions.MANAGE_OCCUPANT_VEHICLES)
                ? {
                      label: 'Add Vehicle',
                      icon: <CarOutlined />,
                      handler: () => handleOpenVehicleModal()
                  }
                : null,
            content: (
                <VehicleTable
                    vehicles={propertyVehicles}
                    members={occupants}
                    globalFilter={globalFilter}
                    onVehicleClick={
                        hasPermission(OrgPermissions.MANAGE_OCCUPANT_VEHICLES)
                            ? vehicle =>
                                  handleOpenVehicleModal(vehicle, MODAL_ACTION_TYPE.EDIT)
                            : null
                    }
                />
            )
        },
        [ORG_PROPERTY_TAB.DEVICES]: {
            label: 'Devices',
            count: devices?.length || 0,
            action: hasPermission(OrgPermissions.MANAGE_ORG_DEVICES)
                ? {
                      label: 'Add Device',
                      icon: <ScanOutlined />,
                      handler: () => handleOpenDeviceModal()
                  }
                : null,
            content: (
                <DeviceTable
                    globalFilter={globalFilter}
                    data={processDeviceData(devices)}
                    onDeviceClick={
                        hasPermission(OrgPermissions.MANAGE_ORG_DEVICES)
                            ? device =>
                                  handleOpenDeviceModal(device, MODAL_ACTION_TYPE.EDIT)
                            : null
                    }
                    deviceTypes={deviceTypes}
                />
            )
        },
        [ORG_PROPERTY_TAB.OCCUPANTS]: {
            label: 'Codes',
            count: otcCodes?.length || 0,
            action: hasPermission(OrgPermissions.MANAGE_ORG_CODES)
                ? {
                      label: 'Create Code',
                      icon: <BarcodeOutlined />,
                      handler: () => handleOpenOtcModal()
                  }
                : null,
            secondaryAction: hasPermission(OrgPermissions.MANAGE_ORG_CODES)
                ? {
                      label: 'Remove Codes',
                      icon: <DeleteOutline />,
                      color: 'error',
                      handler: handleRemoveSelected,
                      disabled: !selectedOtcCodes.length
                  }
                : null,
            content: (
                <CodesTable
                    codes={otcCodes}
                    tPage={otcPage}
                    tRow={otcRow}
                    setTableIndex={setTableIndex}
                    setRowCount={setRowCount}
                    changingFocus={changingFocus}
                    canManage={hasPermission(OrgPermissions.MANAGE_ORG_CODES)}
                    onCodeClick={
                        hasPermission(OrgPermissions.MANAGE_ORG_CODES)
                            ? code => handleOpenOtcModal(code, MODAL_ACTION_TYPE.EDIT)
                            : null
                    }
                    selectedCodes={selectedOtcCodes}
                    onSelectionChange={setSelectedOtcCodes}
                    globalFilter={globalFilter}
                />
            )
        }
    };

    const enabledTabs = useMemo(() => {
        const allowedTabs = { ...TABS };
        console.log('moduleKeys', moduleKeys);
        if (!activeOrg?.modules?.includes(moduleKeys.DEVICES)) {
            delete allowedTabs[ORG_PROPERTY_TAB.DEVICES];
        }
        if (!activeOrg?.modules?.includes(moduleKeys.CREDENTIAL_SHARING_CONSUMER)) {
            delete allowedTabs[ORG_PROPERTY_TAB.ASSOCIATES];
        }

        return allowedTabs;
    }, [activeOrg, TABS]);

    if (!enabledTabs || !propertyId) {
        return <Loader />;
    }

    return (
        <>
            {visitorLoading && <Loader />}
            <TabPanel
                title={title}
                tabs={enabledTabs}
                defaultTab={ORG_PROPERTY_TAB.TEAM_MEMBERS}
                actionComponent={ActionButton}
                actionComponentActions={activeTab => [
                    TABS[activeTab].action,
                    TABS[activeTab].secondaryAction
                ]}
                onTabChange={() => setGlobalFilter('')}
                headerComponent={
                    <TextField
                        variant="outlined"
                        size="small"
                        placeholder="Search..."
                        value={globalFilter}
                        onChange={e => setGlobalFilter(e.target.value)}
                        sx={{ minWidth: 150 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchOutlined />
                                </InputAdornment>
                            )
                        }}
                    />
                }
            />
            {(loading || membersLoading || propertiesLoading) && <Loader />}
        </>
    );
};

const mapStateToProps = ({
    properties,
    vg,
    members,
    user,
    org,
    device,
    manager,
    otc
}) => {
    const {
        property,
        loading: propertiesLoading,
        error,
        organizationProperty
    } = properties;
    const { selectedOrg } = org;
    const { activeOrg, changingFocus, userData } = user;
    const { devices } = device;
    const { loading: membersLoading, error: membersError, member } = members;
    const { otcCodes, otcPage, otcRow } = otc;
    const { orgInviteMembers, orgInviteOperators } = manager;

    const propertyVehicles = getPropertyVehicles(vg, activeOrg, activeOrg?.property_id, [
        userRoles.OPERATOR
    ]);
    const orgVehiclePlates = getOrgVehiclePlates(manager);

    return {
        activeOrg: activeOrg || selectedOrg,
        property,
        member,
        members: vg.members,
        membersLoading,
        membersError,
        propertyVehicles,
        orgVehiclePlates,
        devices: devices || [],
        propertiesLoading,
        error,
        organizationProperty,
        otcCodes,
        otcPage,
        otcRow,
        changingFocus,
        userData,
        orgInviteMembers,
        orgInviteOperators
    };
};

export default connect(mapStateToProps, {
    getPropertyByIdCollection,
    getPropertyMembers,
    removingMember,
    removingPending,
    suspendingPropertyGuest,
    openSnackbar,
    setTableIndex,
    setRowCount
})(MyOrganizationTabPanel);
